import * as React from 'react';

import { Breadcrumbs } from './Breadcrumbs';
import { useEvent } from '../../common/hooks/useEvent.hook';
import { useCallbackMemoize } from '../../common/hooks/useCallbackMemoize.hook';
import { BreadcrumbsEvents } from '../../common/events/breadcrumbs.events';
import { HeaderWrapper } from './Breadcrumbs.style';
import { useData } from 'shell/dataContext';
import { getCurrentStep, getIsNewBuild } from '../data/utils';
import { getIsImpersonated } from '../../common/services/utils';
import restService from '../../../../utils/networkService';
import type { BreadcrumbsData } from '../data/models';
import { TEMPLATE_ID_AD_REVIEW, ExcludeBreadcrumbsWithTemplateIdList } from '../data/constants';

interface FederatedBreadcrumbsProps extends BreadcrumbsData {}

type BreadcrumbsAction =
    | {
          type: 'SET_STEP';
          payload: {
              step: number;
              surveyId: number | string | undefined | null;
              templateId: string | undefined | null;
              params: FederatedBreadcrumbsProps;
          };
      }
    | {
          type: 'SET_REFRESH';
          payload: boolean;
      }
    | {
          type: 'UPDATE_PARAMS';
          payload: Partial<FederatedBreadcrumbsProps>;
      }
    | {
          type: 'RESET';
          payload: FederatedBreadcrumbsProps;
      };

// An interface for our state
interface BreadcrumbsState {
    isShown: boolean;
    step: number;
    refresh: boolean;
    params: FederatedBreadcrumbsProps;
    surveyId: number | undefined;
}

const createInitialState = (params: FederatedBreadcrumbsProps): BreadcrumbsState => ({
    surveyId: undefined,
    isShown: false,
    step: 0,
    refresh: false,
    params,
});

const reducer = (state: BreadcrumbsState, action: BreadcrumbsAction) => {
    switch (action.type) {
        case 'SET_STEP': {
            const isTheSameStep = state.step === action.payload.step;
            const isTheSameSurveyId = state.surveyId && state.surveyId === Number(action.payload.surveyId);
            // Check if the step is really different
            if (isTheSameStep && isTheSameSurveyId && state.isShown) {
                return state;
            }
            const { templateId } = action.payload;
            const isExcluded = typeof templateId === 'string' && ExcludeBreadcrumbsWithTemplateIdList.includes(templateId);
            const isSpecialCase = typeof templateId === 'string' && TEMPLATE_ID_AD_REVIEW.includes(templateId) && !getIsImpersonated();
            const isShown = !(isExcluded || isSpecialCase);
            return {
                ...state,
                isShown: isShown,
                refresh: true,
                step: action.payload.step,
                ...(action.payload.surveyId
                    ? { surveyId: Number(action.payload.surveyId) }
                    : {
                          surveyId: undefined,
                          params: action.payload.params,
                      }),
            };
        }
        case 'SET_REFRESH':
            return { ...state, refresh: action.payload };
        case 'UPDATE_PARAMS':
            return { ...state, params: { ...state.params, ...action.payload } };
        case 'RESET':
            return createInitialState(action.payload);
        default:
            return state;
    }
};

const FederatedBreadcrumbs = React.memo((props: FederatedBreadcrumbsProps) => {
    const [context, dispatch] = React.useReducer(reducer, props, createInitialState);
    const { history, setMediaUploadData, redirectToLogin } = useData();

    const surveyId = context.surveyId ?? context.params?.surveyId;

    const handleCurrentPage = useCallbackMemoize(() => {
        const { success, data } = getCurrentStep();
        if (success) {
            dispatch({
                type: 'SET_STEP',
                payload: { ...data, params: props },
            });
        } else {
            dispatch({ type: 'RESET', payload: props });
        }
    }, [props, dispatch]);

    React.useEffect(() => {
        handleCurrentPage();
        return history?.listen(handleCurrentPage);
    }, [history, handleCurrentPage]);

    React.useEffect(() => {
        setMediaUploadData({ PId: surveyId, Module: 'TQS' });
    }, [surveyId]);

    React.useEffect(() => {
        restService.initInterceptor({
            onUnauthorized: () => redirectToLogin(false),
            onForbidden: () => redirectToLogin(false),
            discardSelector: true,
        });
    }, [history]);

    useEvent({
        name: BreadcrumbsEvents.updateParams,
        handler: (event: CustomEvent) => {
            dispatch({ type: 'UPDATE_PARAMS', payload: event.detail });
        },
    });

    const handleRefresh = React.useCallback(
        (payload: boolean) => {
            dispatch({ type: 'SET_REFRESH', payload });
        },
        [dispatch]
    );

    if (!context.isShown) {
        return null;
    }

    return (
        <HeaderWrapper>
            <div style={{ width: '980px', lineHeight: '35px' }}>
                <Breadcrumbs
                    {...context.params}
                    refresh={context.refresh}
                    setRefresh={handleRefresh}
                    useNewBuild={getIsNewBuild()}
                    baseUrl={window.ts.globals.TIM_BASEAPI_URL}
                    surveyId={surveyId}
                    currentStep={context.step}
                />
            </div>
        </HeaderWrapper>
    );
});

export default FederatedBreadcrumbs;
